import { useState } from 'react';

function formatTimestamp(value) {
  if (!value) {
    return 'Not available';
  }
  return new Date(value).toLocaleString();
}

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'operator', label: 'Operator' },
  { value: 'tenant_admin', label: 'Tenant admin' },
];

const emptyTenantForm = {
  tenant_name: '',
  admin_email: '',
  admin_full_name: '',
};

const emptyMemberForm = {
  email: '',
  full_name: '',
  role: 'viewer',
};

function formatRoleLabel(role) {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label || role || 'Unknown';
}

export default function AccessView({
  auth,
  memberships,
  auditEvents,
  loading,
  error,
  tenantSwitching,
  onSwitchTenant,
  onRefresh,
  onCreateAlphaTenant,
  onCreateMembership,
  onUpdateMembership,
  onDeleteMembership,
}) {
  const canBootstrapTenants = Boolean(auth.can_bootstrap_alpha_tenants);
  const canManageMembers = auth.active_role === 'tenant_admin';
  const [tenantForm, setTenantForm] = useState(emptyTenantForm);
  const [tenantSubmitting, setTenantSubmitting] = useState(false);
  const [tenantResult, setTenantResult] = useState(null);
  const [tenantError, setTenantError] = useState('');
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [memberMessage, setMemberMessage] = useState('');
  const [memberError, setMemberError] = useState('');
  const [membershipActionId, setMembershipActionId] = useState(null);
  const activeTenantLabel = auth.active_tenant_name || auth.active_tenant_id || 'No active tenant';

  const handleTenantSubmit = async (event) => {
    event.preventDefault();
    setTenantSubmitting(true);
    setTenantError('');
    setTenantResult(null);
    try {
      const result = await onCreateAlphaTenant({
        tenant_name: tenantForm.tenant_name.trim(),
        admin_email: tenantForm.admin_email.trim(),
        admin_full_name: tenantForm.admin_full_name.trim() || null,
      });
      setTenantResult(result);
      setTenantForm(emptyTenantForm);
    } catch (requestError) {
      setTenantError(requestError.message);
    } finally {
      setTenantSubmitting(false);
    }
  };

  const handleMemberSubmit = async (event) => {
    event.preventDefault();
    setMemberSubmitting(true);
    setMemberError('');
    setMemberMessage('');
    try {
      const result = await onCreateMembership({
        email: memberForm.email.trim(),
        full_name: memberForm.full_name.trim() || null,
        role: memberForm.role,
      });
      setMemberMessage(`Added ${result.email} as ${result.role}.`);
      setMemberForm(emptyMemberForm);
    } catch (requestError) {
      setMemberError(requestError.message);
    } finally {
      setMemberSubmitting(false);
    }
  };

  const handleRoleChange = async (membership, role) => {
    setMembershipActionId(membership.id);
    setMemberError('');
    setMemberMessage('');
    try {
      const result = await onUpdateMembership(membership.id, { role });
      setMemberMessage(`Updated ${result.email} to ${result.role}.`);
    } catch (requestError) {
      setMemberError(requestError.message);
    } finally {
      setMembershipActionId(null);
    }
  };

  const handleRemoveMembership = async (membership) => {
    setMembershipActionId(membership.id);
    setMemberError('');
    setMemberMessage('');
    try {
      await onDeleteMembership(membership.id);
      setMemberMessage(`Removed ${membership.email}.`);
    } catch (requestError) {
      setMemberError(requestError.message);
    } finally {
      setMembershipActionId(null);
    }
  };

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Admin</p>
            <h2>Identity and tenant controls</h2>
            <p className="panel-copy">Review the active tenant, inspect memberships, and confirm the latest audit activity for this workspace.</p>
          </div>
          <div className="access-actions">
            <button className="secondary-button" type="button" onClick={onRefresh} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>

        {error ? <section className="status-card error-card">{error}</section> : null}

        <div className="findings-summary-grid access-summary-grid">
          <div className="detail-card summary-card access-summary-card">
            <span className="summary-card-label">Current role</span>
            <strong title={formatRoleLabel(auth.active_role)}>{formatRoleLabel(auth.active_role)}</strong>
          </div>
          <div className="detail-card summary-card summary-card-muted access-summary-card">
            <span className="summary-card-label">Active tenant</span>
            <strong title={activeTenantLabel}>{activeTenantLabel}</strong>
          </div>
          <div className="detail-card summary-card access-summary-card">
            <span className="summary-card-label">Memberships</span>
            <strong>{memberships.length}</strong>
          </div>
          <div className="detail-card summary-card access-summary-card">
            <span className="summary-card-label">Audit events</span>
            <strong>{auditEvents.length}</strong>
          </div>
        </div>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Identity</p>
              <h3>Current principal and tenant</h3>
            </div>
          </div>
          <div className="access-grid">
            <section className="detail-card access-card">
              <strong>Current principal</strong>
              <p>{auth.actor_label}</p>
              <p>{auth.email || auth.actor_id}</p>
              <div className="chip-row">
                <span className="chip">Actor type: {auth.actor_type}</span>
                <span className="chip">Role: {formatRoleLabel(auth.active_role)}</span>
              </div>
            </section>

            <section className="detail-card access-card">
              <strong>Active tenant</strong>
              <label className="form-field">
                <span>Tenant</span>
                <select
                  className="text-input"
                  value={auth.active_tenant_id}
                  onChange={(event) => onSwitchTenant(event.target.value)}
                  disabled={tenantSwitching}
                >
                  {auth.memberships.map((membership) => (
                    <option key={membership.id} value={membership.tenant_id}>
                      {membership.tenant_name} · {formatRoleLabel(membership.role)}
                    </option>
                  ))}
                </select>
              </label>
              <p className="panel-copy">Switching tenants clears stale view data before reloading tenant-scoped content.</p>
            </section>
          </div>
        </section>

        {canBootstrapTenants ? (
          <section className="overview-band">
            <div className="chapter-header">
              <div>
                <p className="chapter-kicker">Private alpha</p>
                <h3>Create customer tenant</h3>
              </div>
            </div>
            <section className="detail-card access-card">
              <form className="admin-form" onSubmit={handleTenantSubmit}>
                <div className="access-grid">
                  <label className="form-field">
                    <span>Company / tenant name</span>
                    <input
                      className="text-input"
                      value={tenantForm.tenant_name}
                      onChange={(event) => setTenantForm((current) => ({ ...current, tenant_name: event.target.value }))}
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>First admin email</span>
                    <input
                      className="text-input"
                      type="email"
                      value={tenantForm.admin_email}
                      onChange={(event) => setTenantForm((current) => ({ ...current, admin_email: event.target.value }))}
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>First admin full name</span>
                    <input
                      className="text-input"
                      value={tenantForm.admin_full_name}
                      onChange={(event) => setTenantForm((current) => ({ ...current, admin_full_name: event.target.value }))}
                    />
                  </label>
                </div>
                <button className="primary-button" type="submit" disabled={tenantSubmitting}>
                  {tenantSubmitting ? 'Creating…' : 'Create customer tenant'}
                </button>
              </form>
              {tenantError ? <section className="status-card error-card compact-status">{tenantError}</section> : null}
              {tenantResult ? (
                <section className="status-card success-card compact-status">
                  Created {tenantResult.tenant_name} for {tenantResult.admin_membership.email}. Tenant ID: {tenantResult.tenant_id}
                </section>
              ) : null}
            </section>
          </section>
        ) : null}

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Workspace access</p>
              <h3>Memberships</h3>
            </div>
          </div>
          <section className="detail-card access-card">
            <div className="overview-section-header">
              <strong>Memberships</strong>
              <span className="chip">{memberships.length} total</span>
            </div>
            {canManageMembers ? (
              <form className="admin-form" onSubmit={handleMemberSubmit}>
                <div className="access-grid">
                  <label className="form-field">
                    <span>Email</span>
                    <input
                      className="text-input"
                      type="email"
                      value={memberForm.email}
                      onChange={(event) => setMemberForm((current) => ({ ...current, email: event.target.value }))}
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Full name</span>
                    <input
                      className="text-input"
                      value={memberForm.full_name}
                      onChange={(event) => setMemberForm((current) => ({ ...current, full_name: event.target.value }))}
                    />
                  </label>
                  <label className="form-field">
                    <span>Role</span>
                    <select
                      className="text-input"
                      value={memberForm.role}
                      onChange={(event) => setMemberForm((current) => ({ ...current, role: event.target.value }))}
                    >
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <button className="primary-button" type="submit" disabled={memberSubmitting}>
                  {memberSubmitting ? 'Adding…' : 'Add member'}
                </button>
              </form>
            ) : null}
            {memberError ? <section className="status-card error-card compact-status">{memberError}</section> : null}
            {memberMessage ? <section className="status-card success-card compact-status">{memberMessage}</section> : null}
            {memberships.length ? (
              <div className="stack compact-stack">
                {memberships.map((membership) => (
                  <div className="overview-row-link access-row" key={membership.id}>
                    <div>
                      <strong>{membership.full_name || membership.email}</strong>
                      <p>{membership.email}</p>
                    </div>
                    {canManageMembers ? (
                      <div className="membership-actions">
                        <label className="form-field compact-field">
                          <span>Role</span>
                          <select
                            className="text-input"
                            value={membership.role}
                            onChange={(event) => handleRoleChange(membership, event.target.value)}
                            disabled={membershipActionId === membership.id}
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => handleRemoveMembership(membership)}
                          disabled={membershipActionId === membership.id}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <span className="chip">{formatRoleLabel(membership.role)}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state-card">
                <strong>No memberships found</strong>
                <p>Tenant membership records will appear here once users have been invited or provisioned.</p>
              </div>
            )}
          </section>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Audit</p>
              <h3>Recent audit events</h3>
            </div>
          </div>
          <section className="detail-card access-card">
            <div className="overview-section-header">
              <strong>Recent audit events</strong>
              <span className="chip">{auditEvents.length} shown</span>
            </div>
            <div className="stack compact-stack">
              {auditEvents.map((event) => (
                <div className="detail-card" key={event.id}>
                  <div className="chip-row">
                    <span className="chip chip-emphasis">{event.action_type}</span>
                    <span className="chip">{event.status}</span>
                    <span className="chip">{event.source_type}</span>
                  </div>
                  <p>{event.summary}</p>
                  <p className="panel-copy">{formatTimestamp(event.created_at)}</p>
                </div>
              ))}
              {!auditEvents.length ? (
                <div className="empty-state-card">
                  <strong>No audit events yet</strong>
                  <p>Privileged actions will start appearing here as they are performed inside the current tenant.</p>
                </div>
              ) : null}
            </div>
          </section>
        </section>
      </div>
    </section>
  );
}
